/**
 * Pure feature resolution (B2B Phase A, #1645). Composes the registry default
 * with every override source in a defined precedence, side-effect free so it is
 * fully unit-testable while the React hook stays a thin shell.
 *
 * Precedence (later wins), then a hard tier gate:
 *   1. preset default (registry defaultOn[preset])
 *   2. PostHog flag (rollout/experiment)
 *   3. env override (VITE_FF_FEATURE_*, CI/local)
 *   4. per-firm override (admin toggle — strongest intent)
 *   → then: if enabled AND minTier unmet → disabled (tier is a hard requirement)
 */

import type { Tier } from '@/hooks/useSubscription';
import type { FeatureDef, FeaturePreset } from './registry';

export interface FeatureResolutionContext {
  preset: FeaturePreset;
  /** PostHog opinion for a flag, or undefined for "no opinion". */
  posthog?: (flag: string) => boolean | undefined;
  /** Env override for a feature key, or undefined. */
  env?: (key: string) => boolean | undefined;
  /** Whether the current subscription meets a tier. */
  hasTier?: (min: Tier) => boolean;
  /** Per-firm override for a feature key, or undefined. */
  firmOverride?: (key: string) => boolean | undefined;
}

export interface FeatureResolution {
  enabled: boolean;
  reason: string;
}

export function resolveFeature(def: FeatureDef, ctx: FeatureResolutionContext): FeatureResolution {
  let enabled = def.defaultOn[ctx.preset];
  let reason = `preset:${ctx.preset}`;

  const ph = def.posthogFlag && ctx.posthog ? ctx.posthog(def.posthogFlag) : undefined;
  if (ph !== undefined) { enabled = ph; reason = 'posthog'; }

  const ev = ctx.env ? ctx.env(def.key) : undefined;
  if (ev !== undefined) { enabled = ev; reason = 'env'; }

  const fo = ctx.firmOverride ? ctx.firmOverride(def.key) : undefined;
  if (fo !== undefined) { enabled = fo; reason = 'firm-override'; }

  if (enabled && def.minTier && ctx.hasTier && !ctx.hasTier(def.minTier)) {
    return { enabled: false, reason: `tier<${def.minTier}` };
  }
  return { enabled, reason };
}
